import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { ListingCard } from "@/components/ListingCard";
import { Stars } from "@/components/Stars";
import { ShieldCheck, Repeat, MessageSquare, Crown } from "lucide-react";

export default function Profile() {
  const { id } = useParams();
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    api.get(`/users/${id}`).then(({ data }) => setProfile(data)).catch(() => setProfile(false));
  }, [id]);

  if (profile === null) return <div className="py-20 text-center font-display font-extrabold text-xl animate-pulse">Loading profile…</div>;
  if (profile === false) return <div className="py-20 text-center font-display font-extrabold text-2xl">User not found</div>;

  const since = profile.created_at ? new Date(profile.created_at).getFullYear() : "";

  return (
    <div className="max-w-5xl mx-auto px-5 sm:px-8 py-10">
      {/* Header */}
      <div className="bg-white nb-border rounded-2xl p-6 sm:p-8 nb-shadow flex flex-col sm:flex-row sm:items-center gap-6" data-testid="profile-header">
        {profile.picture ? (
          <img src={profile.picture} alt="" className="w-24 h-24 rounded-2xl nb-border object-cover" />
        ) : (
          <div className="w-24 h-24 rounded-2xl bg-black text-[#E6B31E] nb-border flex items-center justify-center font-display font-black text-4xl">
            {profile.name?.[0]?.toUpperCase()}
          </div>
        )}
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="font-display font-black text-3xl tracking-tighter">{profile.name}</h1>
            {profile.is_member && (
              <span data-testid="profile-member-badge" className="flex items-center gap-1 font-extrabold text-xs bg-[#E6B31E] text-black nb-border rounded-full px-3 py-1">
                <Crown size={13} strokeWidth={3} /> MEMBER
              </span>
            )}
          </div>
          <div className="text-sm text-black/50 font-semibold mt-0.5">Member since {since}</div>
          <div className="flex items-center gap-2 mt-2">
            <Stars value={profile.rating} size={20} />
            <span className="font-extrabold">{profile.rating || "—"}</span>
            <span className="text-sm text-black/50 font-semibold">({profile.review_count} reviews)</span>
          </div>
        </div>
        <div className="flex gap-3">
          <div className="bg-[#E6B31E] text-black nb-border rounded-xl px-5 py-3 text-center">
            <div className="font-display font-black text-2xl">{profile.completed_trades}</div>
            <div className="text-[10px] font-bold uppercase">Trades done</div>
          </div>
          <div className="bg-black text-[#E6B31E] nb-border rounded-xl px-5 py-3 text-center">
            <div className="font-display font-black text-2xl">{profile.listings.length}</div>
            <div className="text-[10px] font-bold uppercase">Active</div>
          </div>
        </div>
      </div>

      {/* Listings */}
      <h2 className="font-display font-extrabold text-2xl mt-10 mb-4 flex items-center gap-2"><Repeat size={22} /> Available to trade</h2>
      {profile.listings.length === 0 ? (
        <div className="text-black/40 font-semibold">No active listings.</div>
      ) : (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 items-start">
          {profile.listings.map((l, i) => <ListingCard key={l.id} listing={l} index={i} />)}
        </div>
      )}

      {/* Reviews */}
      <h2 className="font-display font-extrabold text-2xl mt-10 mb-4 flex items-center gap-2"><MessageSquare size={22} /> Reviews</h2>
      {profile.reviews.length === 0 ? (
        <div className="text-black/40 font-semibold">No reviews yet.</div>
      ) : (
        <div className="space-y-4">
          {profile.reviews.map((r) => (
            <div key={r.id} data-testid={`review-${r.id}`} className="bg-white nb-border rounded-2xl p-5 nb-shadow">
              <div className="flex items-center justify-between">
                <div className="font-extrabold">{r.rater_name}</div>
                <Stars value={r.rating} size={15} />
              </div>
              {r.comment && <p className="text-sm text-black/70 font-medium mt-2">{r.comment}</p>}
              <div className="text-[11px] text-black/40 font-semibold mt-2">on “{r.listing_title}”</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
